import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  setClockInOutButtonVisibilityFailure,
  setClockInOutButtonVisibilitySuccess,
} from 'src/redux/actions/facilityActions';
import therapistService from 'src/services/apiServices/therapist-scheduling/therapistService';

/**
 * Single source of truth (APIT-587) for whether the manual Clock In/Out button
 * and its associated warning pop-ups (on document editing and signature) should
 * be shown for the current facility.
 *
 * Driven by the per-facility flag from
 * `GET /provider/facility/clock-in-out-button/{facilityUuid}` which returns
 * `{ data: boolean }`:
 *   - true  -> show the button / warnings
 *   - false / 404 / any error -> hide (handled here + in saga/reducer)
 *
 * Fetch strategy:
 *   - The dashboard container fetches ONCE on load (single call, not per-consumer),
 *     and the login/facility-switch sagas refetch when the facility context changes.
 *   - For document operations (edit/sign), the handler awaits `refetch()` so the
 *     clock-in popup decision uses the LATEST server value for that exact action,
 *     without a page/tab refresh.
 *
 * Hide-until-resolved: `isVisible` is only true once resolved AND the flag is true,
 * so consumers never flash a stale value before the fetch lands.
 */
const useClockInOutButtonVisibility = () => {
  const dispatch = useDispatch();

  const isVisible = useSelector(
    (state: any) => state?.clockInOutButtonVisibilityReducer?.isVisible ?? false
  );
  const resolved = useSelector(
    (state: any) => state?.clockInOutButtonVisibilityReducer?.resolved ?? false
  );

  /**
   * Fetch the per-facility flag NOW, update redux (so the button UI stays in
   * sync), and resolve to the live boolean. Awaitable so a document-operation
   * handler can gate its clock-in popup on the just-fetched value.
   * Any error/404 -> false (hide).
   */
  const refetch = useCallback(async (): Promise<boolean> => {
    try {
      const facilityUuid = localStorage.getItem('facilityId') || '';
      if (!facilityUuid) {
        dispatch(setClockInOutButtonVisibilityFailure());
        return false;
      }
      const response = await therapistService.getClockInOutButtonVisibility(facilityUuid);
      if (response?.status === 200) {
        const nextVisible = Boolean(response?.data?.data);
        dispatch(setClockInOutButtonVisibilitySuccess(nextVisible));
        return nextVisible;
      }
      dispatch(setClockInOutButtonVisibilityFailure());
      return false;
    } catch {
      dispatch(setClockInOutButtonVisibilityFailure());
      return false;
    }
  }, [dispatch]);

  // Hide until the flag has been resolved, then apply the real value.
  const showClockInOutButton = resolved && isVisible;

  return { showClockInOutButton, refetch };
};

export default useClockInOutButtonVisibility;
